import React from 'react';
import { ContactsAddresses } from './types';
import { CategoryDropdown } from '../category';
import { AddressSearchSection } from '../addresses';
import { Address } from '../addresses/types';
import '../../../components/common/CRMForm.css';

interface AddressSectionProps {
  contactsAddresses: ContactsAddresses[];
  onChange: (contactsAddresses: ContactsAddresses[]) => void;
}

/** Build Address for display from contactsAddress (has addressId and optional address fields from server join or selection) */
const contactsAddressToSelectedAddress = (ca: ContactsAddresses): Address | null => {
  if (!ca.addressId) return null;
  return {
    id: ca.addressId,
    street1: ca.street1 || '',
    street2: ca.street2,
    unit: ca.unit,
    city: ca.city || '',
    state: ca.state || '',
    zip: ca.zip || '',
    country: ca.country || 'US',
    verified: false,
    dateCreated: '',
    dateUpdated: '',
    whoUpdated: ''
  };
};

const AddressSection: React.FC<AddressSectionProps> = ({ contactsAddresses, onChange }) => {
  const addAddress = () => {
    const newContactsAddress: ContactsAddresses = {
      id: 0,
      contactId: 0,
      addressId: 0,
      typeId: 0,
      description: '',
      isPrimary: contactsAddresses.length === 0
    };
    onChange([...contactsAddresses, newContactsAddress]);
  };

  const updateContactsAddress = (index: number, field: keyof ContactsAddresses, value: unknown) => {
    let updated = contactsAddresses.map((ca, i) => {
      if (i !== index) {
        if (field === 'isPrimary' && value) return { ...ca, isPrimary: false };
        return ca;
      }
      return { ...ca, [field]: value };
    });
    if (field === 'isPrimary' && value) {
      updated = updated.map((ca, i) => ({ ...ca, isPrimary: i === index }));
    }
    if (field === 'isPrimary' && !value) {
      const hasPrimary = updated.some(a => a.isPrimary);
      if (!hasPrimary && updated.length > 0) updated[0].isPrimary = true;
    }
    onChange(updated);
  };

  const handleAddressSelect = (index: number, address: Address | null) => {
    const updated = contactsAddresses.map((ca, i) => {
      if (i !== index) return ca;
      if (!address) return { ...ca, addressId: 0, street1: undefined, street2: undefined, unit: undefined, city: undefined, state: undefined, zip: undefined, country: undefined };
      return {
        ...ca,
        addressId: address.id,
        street1: address.street1,
        street2: address.street2,
        unit: address.unit,
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country || 'US'
      };
    });
    onChange(updated);
  };

  const removeAddress = (index: number) => {
    const updated = contactsAddresses.filter((_, i) => i !== index);
    const removed = contactsAddresses[index];
    if (removed?.isPrimary && updated.length > 0) updated[0].isPrimary = true;
    onChange(updated);
  };

  return (
    <div className="form-section-content">
      {contactsAddresses.map((contactsAddress, index) => (
        <div key={contactsAddress.id || `temp-${index}`} className="form-item-group">
          <div className="app-form-row" style={{ flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div className="app-form-group" style={{ minWidth: '140px' }}>
              <label className="app-form-label">Address Type</label>
              <CategoryDropdown
                parentType="address"
                value={contactsAddress.typeId}
                onChange={(value) => updateContactsAddress(index, 'typeId', value)}
                required={true}
                className={contactsAddress.typeId === 0 ? 'error' : ''}
              />
              {contactsAddress.typeId === 0 && (
                <div className="app-form-error">Please select an address type</div>
              )}
            </div>
            <div className="app-form-group" style={{ flex: 1, minWidth: '120px' }}>
              <label className="app-form-label">Description</label>
              <input
                type="text"
                className="app-form-input"
                value={contactsAddress.description || ''}
                onChange={(e) => updateContactsAddress(index, 'description', e.target.value)}
                placeholder="e.g., Home, Work, Vacation, etc."
              />
            </div>
            <div className="app-form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
              <label className="app-form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={contactsAddress.isPrimary}
                  onChange={(e) => updateContactsAddress(index, 'isPrimary', e.target.checked)}
                />
                Primary address
              </label>
            </div>
          </div>

          <div className="app-form-group">
            <AddressSearchSection
              selectedAddress={contactsAddressToSelectedAddress(contactsAddress)}
              onAddressSelect={(addr) => handleAddressSelect(index, addr)}
              label="Search Address"
              placeholder="Search addresses by street, city, state, or zip..."
              availableForProperty={false}
            />
            {(!contactsAddress.addressId || contactsAddress.addressId === 0) && (
              <div className="app-form-error">Please search and select an address</div>
            )}
          </div>

          <div className="form-item-actions">
            <button
              type="button"
              className="app-btn-delete"
              onClick={() => removeAddress(index)}
              title="Delete address"
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        className="app-btn app-btn-secondary"
        onClick={addAddress}
      >
        Add Address
      </button>

      {contactsAddresses.length > 0 && !contactsAddresses.some(a => a.isPrimary) && (
        <div className="form-warning">
          No primary address selected. Please mark one as primary.
        </div>
      )}
    </div>
  );
};

export default AddressSection;
